/**
 * Global paths and helpers for ~/.trace/ directory structure.
 *
 * All trace state lives here:
 *   ~/.trace/.config.json          — global config
 *   ~/.trace/registry.json         — project registry
 *   ~/.trace/index/<name>-<hash>.db — per-project databases
 *
 * Machines that still have `~/.trace-mcp/` (pre-rename installs) get it
 * renamed to `~/.trace/` the first time this module loads — see
 * `migrateLegacyHomeDir` below.
 */

import crypto from 'node:crypto';
import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';

// Inlined rather than importing ./utils/path-migration.js — this module must
// stay free of cross-module imports (see ensureGlobalDirs() below for why:
// tests/cli/env-overrides.test.ts runs global.ts under
// `node --experimental-strip-types`, which can't resolve .js -> .ts imports
// of sibling modules).
function isSymlink(targetPath: string): boolean {
  try {
    return fs.lstatSync(targetPath).isSymbolicLink();
  } catch {
    return false;
  }
}

/**
 * One-time rename `~/.trace-mcp` -> `~/.trace` (TRA-611). A same-volume
 * `renameSync` is atomic and instant regardless of DB size, unlike a
 * copy-based migration which would leave a stale, disk-doubling duplicate
 * behind (nothing then ever deletes the old copy). Runs at most once per
 * machine: once `target` exists this — and every future call, since
 * TRACE_MCP_HOME below only calls it when `target` is still missing —
 * short-circuits immediately.
 *
 * Skipped under Vitest (`process.env.VITEST`, set by the runner itself and
 * inherited by any subprocess a test spawns): this runs at *import* time, so
 * without the guard, any test that imports this module with no
 * TRACE_MCP_DATA_DIR override — e.g. tests/cli/env-overrides.test.ts's
 * "falls back to default" case, which deliberately clears the override to
 * exercise real os.homedir() resolution in a spawned subprocess — would
 * silently rename a real developer's `~/.trace-mcp` on disk. In-process tests
 * are already protected by tests/setup/isolate-home.ts pinning
 * TRACE_MCP_DATA_DIR before any project module loads; this guard covers the
 * one path that pins the var away on purpose.
 */
/**
 * Durable marker left inside the new home the moment the rename succeeds.
 * `TRACE_MCP_HOME_MIGRATED` below is only `true` for the single process that
 * performed the rename — too narrow a signal for `installLegacyBinCompat()`
 * (src/init/launcher.ts), which needs to retry creating the legacy compat
 * symlink on a *later* `trace init`/`upgrade` run if it failed (or was
 * skipped by a dry-run) the first time. This file existing is the durable
 * "a migration happened here at some point" fact that survives restarts.
 */
export const LEGACY_MIGRATION_MARKER = '.migrated-from-trace-mcp';

function migrateLegacyHomeDir(target: string, legacy: string): boolean {
  if (process.env.VITEST) return false;
  try {
    if (fs.existsSync(target)) return false;
    if (isSymlink(target) || isSymlink(legacy)) return false;
    if (!fs.statSync(legacy).isDirectory()) return false;
    fs.renameSync(legacy, target);
    try {
      fs.writeFileSync(path.join(target, LEGACY_MIGRATION_MARKER), '');
    } catch {
      /* best-effort — worst case a later run just doesn't retry the symlink */
    }
    return true;
  } catch (err) {
    // ENOENT means there is no legacy dir at all — a clean install, nothing to
    // say. Anything else (cross-device rename on a mounted $HOME, permissions)
    // means the data IS there and we are about to start from an empty home
    // instead: indistinguishable from a fresh install unless we say so. The
    // old directory is left untouched, so the fix is a manual move (TRA-732).
    if ((err as NodeJS.ErrnoException).code !== 'ENOENT') {
      console.warn(
        `[trace] Could not migrate "${legacy}" to "${target}": ${(err as Error).message}\n` +
          `[trace] Starting with an empty "${target}". Your existing projects, decision ` +
          `memory and indexes are still in "${legacy}" — move that directory to ` +
          `"${target}" manually to keep them.`,
      );
    }
    return false;
  }
}

/**
 * Root of all trace-mcp global state.
 *
 * Default: `~/.trace/`. Override with `TRACE_MCP_DATA_DIR=<path>` for
 * Docker volumes, ephemeral CI workspaces, multi-repo orchestrators, or
 * shared cache locations. CRG v2.3.0 (#155) introduced the same knob — the
 * env var replaces the default verbatim, with `~` expansion. Resolved at
 * import time so a user-facing change requires a process restart.
 */
const { home: TRACE_MCP_HOME_RESOLVED, migrated: TRACE_MCP_HOME_MIGRATED_RESOLVED } = (() => {
  const override =
    process.env.TRACE_MCP_DATA_DIR || process.env.TRACE_MCP_HOME || process.env.TRACE_HOME;
  if (override && override.length > 0) {
    const expanded = override.startsWith('~')
      ? path.join(os.homedir(), override.slice(1))
      : override;
    return { home: path.resolve(expanded), migrated: false };
  }
  const homedir = os.homedir();
  const target = path.join(homedir, '.trace');
  const legacy = path.join(homedir, '.trace-mcp');
  return { home: target, migrated: migrateLegacyHomeDir(target, legacy) };
})();

export const TRACE_MCP_HOME = TRACE_MCP_HOME_RESOLVED;

/**
 * True only for the single run that performed the `~/.trace-mcp` -> `~/.trace`
 * rename above. Consumed by `src/init/launcher.ts` to know when a compat
 * symlink for scripts hardcoded to the old `bin/trace-mcp` path needs to be
 * (re)created — that symlink then persists on disk, so later runs (where this
 * is always `false`) don't need to touch it again.
 */
export const TRACE_MCP_HOME_MIGRATED = TRACE_MCP_HOME_MIGRATED_RESOLVED;

/** Global config file (replaces per-project .trace-mcp.json). */
export const GLOBAL_CONFIG_PATH = path.join(TRACE_MCP_HOME, '.config.json');

/** Directory for per-project SQLite databases. */
export const INDEX_DIR = path.join(TRACE_MCP_HOME, 'index');

/**
 * Index DBs for one-shot agent-run checkouts (TRA-396). Kept in their own
 * subdirectory because those roots are never written to registry.json, so a
 * registry-driven sweep can't reach their DBs and `prune` can only class them
 * as `orphan_unregistered` — a category soft GC deliberately never deletes,
 * since for a *normal* project it just means "not re-added yet". The
 * directory is the marker that makes them safely collectable by age; see
 * `sweepEphemeralDbs` in registry.ts.
 */
export const EPHEMERAL_INDEX_DIR = path.join(INDEX_DIR, 'ephemeral');

/**
 * Path shape Multica's `repo checkout` uses for one-shot agent-run workdirs:
 * `.../multica_workspaces_<host>/<workspace-id>/<run-id>/workdir`. Each task
 * run gets a brand-new directory, so a project matching this shape is queried
 * exactly once, for the lifetime of that single run, and never touched again.
 *
 * TRA-527: the run directory is the container, not the project root — `repo
 * checkout` clones into `workdir/<repo>`, and a monorepo package sits deeper
 * still. Anchoring on `workdir$` therefore missed every root an agent actually
 * opens, and those leaked back into registry.json (three on the reported
 * machine within hours of TRA-396 shipping). Nothing below a run's workdir
 * outlives the run, so match the whole subtree.
 */
const EPHEMERAL_WORKDIR_PATTERN =
  /[/\\]multica_workspaces[^/\\]*[/\\][^/\\]+[/\\][^/\\]+[/\\]workdir([/\\]|$)/i;

/**
 * The same runtime's other one-shot layout: a task given a scratch directory
 * instead of a workspace checkout lands in `<tmp>/multica-task-<run-id>/...`.
 * `EPHEMERAL_WORKDIR_PATTERN` above never matched those, so each was persisted
 * like a real project — 17 of the 37 rows in the reported registry.json, all
 * dead within the hour, each pinning a `.config.json` section for the full
 * 7-day `sweepMissingRoots` grace (TRA-992).
 *
 * The container is matched, not the leaf: a run drops several roots under it
 * (its own scratch dirs, benchmark fixtures) and none outlive the run. The
 * numeric run id keeps this off a user directory that merely says
 * "multica-task".
 *
 * Note for tests: such a runtime also exports TMPDIR as its own task
 * directory, so `os.tmpdir()` itself can match this. A fixture that must stay
 * persistent has to be built outside it — see `tmpRootOutsideTaskDir` in
 * tests/test-utils.ts.
 */
const EPHEMERAL_TASK_DIR_PATTERN = /[/\\]multica-task-\d+[/\\]/i;

/**
 * Claude Code / Desktop scratchpad directory (TRA-1197):
 * E.g. `/private/tmp/claude-501/.../scratchpad`
 * These scratchpads are one-shot run checkouts that should not be permanently
 * registered in registry.json or pinned in .config.json.
 */
const EPHEMERAL_CLAUDE_SCRATCHPAD_PATTERN =
  /[/\\]claude-\d+[/\\][^/\\]+[/\\][^/\\]+[/\\]scratchpad([/\\]|$)/i;

/**
 * True when `root` is a one-shot agent-run checkout, in either layout the
 * runtime uses (see {@link EPHEMERAL_WORKDIR_PATTERN} and
 * {@link EPHEMERAL_TASK_DIR_PATTERN}), or matches a user-configured
 * `auto_register.exclude` glob (GH#1371 / TRA-1881: throwaway worktrees,
 * bare-mirror checkouts, /tmp clones, CI gates). Such roots are never
 * persisted to registry.json — see the `_ephemeralEntries` note in
 * registry.ts — and their DBs land in {@link EPHEMERAL_INDEX_DIR} via
 * {@link getDbPath}, where age-based eviction can collect them.
 *
 * Lives here rather than in registry.ts (which re-exports it) because
 * {@link getDbPath} needs it, and global.ts must stay import-free.
 */
export function isEphemeralProjectRoot(root: string): boolean {
  const abs = path.resolve(root);
  return (
    EPHEMERAL_WORKDIR_PATTERN.test(abs) ||
    EPHEMERAL_TASK_DIR_PATTERN.test(abs) ||
    EPHEMERAL_CLAUDE_SCRATCHPAD_PATTERN.test(abs) ||
    isUserExcludedProjectRoot(abs)
  );
}

/**
 * Auto-registration mode from the global config (`auto_register.mode`,
 * GH#1371 / TRA-1881). `'always'` (default) preserves historic behavior.
 * `'never'` disables every implicit registration; `'ask'` is accepted but
 * currently behaves as `'never'` (no interactive prompt exists in the daemon
 * path yet — see the `auto_register` template comment).
 *
 * `TRACE_MCP_AUTO_REGISTER_MODE` overrides the file when set (CI gates).
 * Sync + cached: safe to call from registration hot paths.
 */
export type AutoRegisterMode = 'always' | 'never' | 'ask';

export function getAutoRegisterMode(): AutoRegisterMode {
  return readAutoRegisterConfig().mode;
}

/**
 * True when `root` (absolute or relative) matches a user-configured
 * `auto_register.exclude` glob. Patterns are matched against the resolved
 * root after `~` and `$VAR`/`${VAR}` expansion (so `$TMPDIR/**`, `~/.gate/**`
 * work). A pattern without glob magic matches the directory itself and
 * everything under it; a trailing `/**` also matches the base directory.
 *
 * Extra patterns can also come from `TRACE_MCP_AUTO_REGISTER_EXCLUDE`
 * (comma-separated, same syntax) — useful for CI gates that cannot edit the
 * global config file.
 */
export function isUserExcludedProjectRoot(root: string): boolean {
  const abs = path.resolve(root);
  const patterns = readAutoRegisterExcludePatterns();
  // Candidate (root, pattern) pairs: the as-given forms plus best-effort
  // realpath variants on both sides. On macOS `/tmp` symlinks to
  // `/private/tmp` and `path.resolve` doesn't resolve symlinks, so a `/tmp/**`
  // pattern under-matches an already-realpath'd root (and a `/private/tmp/**`
  // pattern under-matches a symlinked root) without this.
  const roots = [abs];
  try {
    const real = fs.realpathSync(abs);
    if (real !== abs) roots.push(real);
  } catch {
    /* missing dir — the abs check below already ran */
  }
  const allPatterns = [...patterns];
  for (const pattern of patterns) {
    const variant = realpathAutoRegisterPatternBase(pattern);
    if (variant && variant !== pattern) allPatterns.push(variant);
  }
  for (const r of roots) {
    for (const pattern of allPatterns) {
      if (autoRegisterPatternMatches(r, pattern)) return true;
    }
  }
  return false;
}

/**
 * Best-effort realpath of the literal leading directory of an exclude glob
 * (the part before the first `*?[{` magic, cut back to the last `/`), with
 * the glob tail re-attached. Returns null when there is nothing to resolve
 * (no magic, unresolvable prefix, or already canonical). Lets a `/tmp/**`
 * pattern match a `/private/tmp/...` root and vice versa.
 */
function realpathAutoRegisterPatternBase(pattern: string): string | null {
  try {
    const expanded = expandAutoRegisterPattern(pattern);
    const normalized = expanded.replace(/\\/g, '/');
    const magicIdx = normalized.search(/[*?[\]{}]/);
    if (magicIdx === -1) {
      const real = fs.realpathSync(expanded).replace(/\\/g, '/');
      return real !== normalized ? real : null;
    }
    const head = normalized.slice(0, magicIdx);
    const slash = head.lastIndexOf('/');
    if (slash <= 0) return null;
    const dirHead = head.slice(0, slash) || '/';
    const realDir = fs.realpathSync(dirHead).replace(/\\/g, '/');
    if (realDir === dirHead) return null;
    return realDir + normalized.slice(slash);
  } catch {
    return null;
  }
}

// The `auto_register` slice of the global config. Read fresh on every call:
// the file is tiny (~1 KB), callers are registration paths (not per-tool-call
// hot paths), and a cache keyed on mtime+size risks serving stale excludes
// when the file is rewritten within one timestamp tick (notably in tests).
function readAutoRegisterConfig(): { mode: AutoRegisterMode; exclude: string[] } {
  let mode: AutoRegisterMode = 'always';
  let exclude: string[] = [];
  try {
    const rawText = fs.readFileSync(GLOBAL_CONFIG_PATH, 'utf8');
    const parsed: unknown = JSON.parse(stripJsonComments(rawText));
    const section =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>).auto_register
        : null;
    if (typeof section === 'object' && section !== null) {
      const m = (section as Record<string, unknown>).mode;
      if (m === 'never' || m === 'ask' || m === 'always') mode = m;
      const e = (section as Record<string, unknown>).exclude;
      if (Array.isArray(e)) exclude = e.filter((v): v is string => typeof v === 'string');
    }
  } catch {
    /* corrupt config — validate elsewhere; fall back to defaults here */
  }
  const envMode = (process.env.TRACE_MCP_AUTO_REGISTER_MODE ?? '').trim().toLowerCase();
  if (envMode === 'always' || envMode === 'never' || envMode === 'ask') mode = envMode;
  const envExclude = (process.env.TRACE_MCP_AUTO_REGISTER_EXCLUDE ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (envExclude.length > 0) exclude = [...exclude, ...envExclude];
  return { mode, exclude };
}

function readAutoRegisterExcludePatterns(): string[] {
  return readAutoRegisterConfig().exclude;
}

function expandAutoRegisterPattern(pattern: string): string {
  let out = pattern.trim();
  if (!out) return out;
  const home = os.homedir();
  if (out === '~' || out.startsWith('~/') || out.startsWith('~\\')) {
    out = path.join(home, out.slice(2));
  }
  out = out.replace(/\$([A-Za-z_][A-Za-z0-9_]*)|\$\{([^}]+)\}/g, (m, v1, v2) => {
    const name: string = v1 ?? v2;
    return process.env[name] ?? m;
  });
  // `$TMPDIR` on macOS ends with `/`, so `$TMPDIR/**` expands to a double
  // slash that would never match a resolved root (`/a//b/**` vs `/a/b/c`).
  out = out.replace(/([^:]|^)\/{2,}/g, '$1/');
  return out;
}

function autoRegisterGlobToRegExp(glob: string): RegExp {
  const g = glob.replace(/\\/g, '/');
  let re = '';
  let i = 0;
  while (i < g.length) {
    const c = g[i];
    if (c === '*') {
      if (g[i + 1] === '*') {
        if (g[i + 2] === '/') {
          re += '(?:.*/)?';
          i += 3;
        } else {
          re += '.*';
          i += 2;
        }
      } else {
        re += '[^/]*';
        i += 1;
      }
    } else if (c === '?') {
      re += '[^/]';
      i += 1;
    } else if (c === '[') {
      const close = g.indexOf(']', i + 1);
      if (close === -1) {
        re += '\\[';
        i += 1;
      } else {
        // Glob negation `[!a]` is regex `[^a]` — without the translation the
        // `!` compiles literally and the class matches `!`+`a` instead.
        const body = g.slice(i, close + 1);
        re += body.startsWith('[!') ? `[^${body.slice(2)}` : body;
        i = close + 1;
      }
    } else if ('+()|^$.{}\\'.includes(c)) {
      re += `\\${c}`;
      i += 1;
    } else {
      re += c;
      i += 1;
    }
  }
  return new RegExp(`^${re}$`);
}

function autoRegisterPatternMatches(absRoot: string, rawPattern: string): boolean {
  const expanded = expandAutoRegisterPattern(rawPattern);
  if (!expanded) return false;
  const isWin = process.platform === 'win32';
  const normRoot = absRoot.replace(/\\/g, '/');
  const normPattern = expanded.replace(/\\/g, '/');
  const root = isWin ? normRoot.toLowerCase() : normRoot;
  const pattern = isWin ? normPattern.toLowerCase() : normPattern;
  const hasMagic = /[*?[\]{}]/.test(pattern);

  // Bare directory without glob magic: the dir itself and everything under it.
  if (!hasMagic) {
    const base = pattern.replace(/\/+$/, '');
    return root === base || root.startsWith(`${base}/`);
  }

  // Trailing /** matches the base directory itself AND everything under it
  // (`/tmp/**` vs `/tmp`): strip it, match the base as a glob, and allow an
  // optional `/...` suffix. Without this, `/**` compiles to `/.*` and a bare
  // base with no trailing slash (`/tmp`) never matches.
  let optionalSuffix = false;
  let effective = pattern;
  if (effective.endsWith('/**')) {
    effective = effective.slice(0, -3);
    optionalSuffix = true;
  }
  // Pattern is exactly `/**` — matches every absolute root.
  if (optionalSuffix && (effective === '' || effective === '**')) return true;

  let rx: RegExp;
  try {
    rx = autoRegisterGlobToRegExp(effective);
  } catch {
    return false;
  }
  if (rx.test(root)) return true;
  if (optionalSuffix) {
    try {
      if (autoRegisterGlobToRegExp(`${effective}/**`).test(root)) return true;
    } catch {
      /* fall through to the relative-pattern check below */
    }
    // Base contains no magic and the checks above missed on case/edge —
    // belt-and-braces prefix comparison (covers `/tmp/**` vs `/tmp` when the
    // base itself is a plain directory).
    if (!/[*?[\]{}]/.test(effective)) {
      const base = effective.replace(/\/+$/, '');
      if (root === base || root.startsWith(`${base}/`)) return true;
    }
  }
  // Relative patterns (no leading slash / drive) match at any depth.
  if (!pattern.startsWith('/') && !/^[a-z]:\//i.test(pattern) && !pattern.startsWith('**/')) {
    try {
      if (autoRegisterGlobToRegExp(`**/${pattern}`).test(root)) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

/**
 * True when `installPath` is an install location that must never back the
 * live daemon (TRA-1807).
 *
 * A postinstall running from an agent-run sandbox (`npm install` inside
 * `/private/tmp/multica-task-<id>/...`) used to overwrite `launcher.env` +
 * the `~/.trace/bin/trace` shim with its own sandbox `dist/cli.js` and then
 * `launchctl kickstart` the live daemon — pinning `:3741` to a directory
 * that vanishes with the run. The daemon kept serving from a deleted tree:
 * every tree-sitter WASM load failed with ENOENT and the extract pool
 * disabled itself permanently, with no self-recovery.
 *
 * This is the install-path twin of {@link isEphemeralProjectRoot}: it matches
 * the same one-shot run shapes, plus the shared tmp roots a stable global
 * install never lives under (`/tmp`, `/private/tmp`). Deliberately NOT the
 * whole `os.tmpdir()` — on macOS that is `/var/folders/.../T`, which also
 * hosts legitimate test fixtures and ad-hoc checkouts.
 *
 * Consumed by:
 * - `serve-http` (fail-closed at startup),
 * - `scripts/postinstall-control-plane.mjs` (refuses to adopt the live
 *   daemon; mirrored there as plain regexes — keep in sync, TRA-1807).
 */
export function isEphemeralInstallPath(installPath: string): boolean {
  const abs = path.resolve(installPath);
  if (
    EPHEMERAL_WORKDIR_PATTERN.test(abs) ||
    EPHEMERAL_TASK_DIR_PATTERN.test(abs) ||
    EPHEMERAL_CLAUDE_SCRATCHPAD_PATTERN.test(abs)
  ) {
    return true;
  }
  // Shared tmp roots, matched on every OS (defense in depth: refusing a
  // daemon install from `/tmp` is harmless on Windows too). Compare on a
  // forward-slash form with any drive letter stripped, because on Windows
  // `path.resolve('/tmp/...')` yields `C:\tmp\...` and a literal `/tmp`
  // prefix never matches (TRA-1810). Keep the separator boundary so
  // `/tmpfoo` or `/private/tmp-backup` don't match.
  const normalized = abs.replace(/\\/g, '/').replace(/^[A-Za-z]:/, '');
  for (const tmpRoot of ['/tmp', '/private/tmp']) {
    if (normalized === tmpRoot || normalized.startsWith(`${tmpRoot}/`)) {
      return true;
    }
  }
  return false;
}

/**
 * True when `entryPath` (a cli entry file) lives inside a dev checkout —
 * a `.git` directory within a few levels above it.
 *
 * TRA-1807: developers running `serve-http` from their own working tree
 * (including the project's own daemon-spawn tests, which exercise the
 * built dist/ of the checkout) are deliberate invocations, not a hijacked
 * live daemon — the ephemeral-install guard exempts them. The hijack vector
 * is npm-installed trees, which never carry `.git`. Mirrors the dev-checkout
 * skips in postinstall (`isDevCheckout`) and the updater.
 */
export function isDevCheckoutEntry(entryPath: string): boolean {
  let dir = path.dirname(path.resolve(entryPath));
  for (let i = 0; i < 4; i++) {
    try {
      if (fs.existsSync(path.join(dir, '.git'))) return true;
    } catch {
      return false;
    }
    const parent = path.dirname(dir);
    if (parent === dir) return false;
    dir = parent;
  }
  return false;
}

/** Global project registry. */
export const REGISTRY_PATH = path.join(TRACE_MCP_HOME, 'registry.json');

/** Global sessions directory (snapshots, end logs, resume summaries). */
export const SESSIONS_DIR = path.join(TRACE_MCP_HOME, 'sessions');

/** Topology database (cross-service graph). */
export const TOPOLOGY_DB_PATH = path.join(TRACE_MCP_HOME, 'topology.db');

/** Decision memory database (cross-session knowledge graph). */
export const DECISIONS_DB_PATH = path.join(TRACE_MCP_HOME, 'decisions.db');

/** Agent execution state database (SKILL.state engine). */
export const STATE_DB_PATH = path.join(TRACE_MCP_HOME, 'state.db');

/** Per-project + per-operation PID lock files (see src/utils/pid-lock.ts). */
export const LOCKS_DIR = path.join(TRACE_MCP_HOME, 'locks');

/**
 * Cross-process sentinel directory: heartbeat/status files, consultation
 * markers, the guard bypass file — everything the MCP server writes for the
 * guard hook and the desktop app to read.
 *
 * Deliberately NOT `$TMPDIR` (TRA-869). TMPDIR is per-process, not per-machine:
 * the writer (an MCP server the client spawned) and the readers (the guard hook
 * the agent harness spawns, the desktop app launchd starts) routinely hold
 * different values — macOS defaults to a per-user `/var/folders/.../T`, and any
 * task runner sets its own. Measured on a live machine: the server was writing
 * `/var/folders/.../T/trace-mcp-alive-d1c1b6f267c7` and refreshing it every 5s
 * while the hook looked in `/tmp/multica-task-.../` and reported "trace-mcp
 * server not running" on every single call — so the guard degraded to allowing
 * the Read/Grep fallback this product exists to replace, against a healthy,
 * connected session.
 */
export const STATUS_DIR = path.join(TRACE_MCP_HOME, 'status');

/** Default port the daemon listens on. Override with TRACE_MCP_DAEMON_PORT. */
export const DEFAULT_DAEMON_PORT =
  process.env.TRACE_MCP_DAEMON_PORT && !Number.isNaN(Number(process.env.TRACE_MCP_DAEMON_PORT))
    ? Number(process.env.TRACE_MCP_DAEMON_PORT)
    : 3741;

/** Daemon log file path. */
export const DAEMON_LOG_PATH = path.join(TRACE_MCP_HOME, 'daemon.log');

/**
 * Opt-out sentinel for the background daemon (#202). When this file exists,
 * `ensureDaemon`/`tryAutoSpawnDaemon` treat the daemon as explicitly disabled:
 * they do NOT (re)install the launchd plist or spawn a detached process. Written
 * by `trace-mcp daemon stop`, cleared by `trace-mcp daemon start`/`restart`.
 * Lets a user who prefers pure stdio remove the daemon and have it stay gone.
 */
export const DAEMON_DISABLED_PATH = path.join(TRACE_MCP_HOME, 'daemon.disabled');

/** launchd plist path for auto-start on macOS. */
export const LAUNCHD_PLIST_PATH = path.join(
  os.homedir(),
  'Library',
  'LaunchAgents',
  'com.trace-mcp.server.plist',
);

/**
 * Default global config template with all supported parameters.
 * Written to ~/.trace-mcp/.config.json on first run so users can edit values
 * instead of looking up parameter names in docs.
 */
/**
 * Strip single-line // comments from JSONC text and fix trailing commas.
 * Handles // inside quoted strings correctly.
 */
export function stripJsonComments(text: string): string {
  let result = '';
  let i = 0;
  while (i < text.length) {
    // String literal — copy verbatim
    if (text[i] === '"') {
      const start = i;
      i++; // opening quote
      while (i < text.length && text[i] !== '"') {
        if (text[i] === '\\') i++; // skip escaped char
        i++;
      }
      i++; // closing quote
      result += text.slice(start, i);
      continue;
    }
    // Line comment — skip to end of line
    if (text[i] === '/' && text[i + 1] === '/') {
      while (i < text.length && text[i] !== '\n') i++;
      continue;
    }
    result += text[i];
    i++;
  }
  // Remove trailing commas before } or ]
  return result.replace(/,(\s*[}\]])/g, '$1');
}

/** Default config template as JSONC (with comments). */
export const DEFAULT_CONFIG_JSONC = `{
  // ── AI / Embeddings ──────────────────────────────────────────────
  "ai": {
    "enabled": false,
    "provider": "onnx",                          // "onnx" (local, zero-config) | "ollama" | "openai"
    // "base_url": "http://localhost:11434",     // custom endpoint (ollama/openai)
    // "api_key": "",                            // required for openai; or set OPENAI_API_KEY env
    // "inference_model": "gemma4:e4b",          // ollama: "gemma4:e4b", openai: "gpt-4o-mini"
    // "fast_model": "gemma4:e4b",               // ollama: "gemma4:e4b", openai: "gpt-4o-mini"
    // "embedding_model": "",                    // onnx: "Xenova/all-MiniLM-L6-v2", ollama: "qwen3-embedding:0.6b", openai: "text-embedding-3-small"
    // "embedding_dimensions": 384,              // onnx: 384, openai: 1536
    "summarize_on_index": false,
    "summarize_batch_size": 20,
    "summarize_kinds": ["class", "function", "method", "interface", "trait", "enum", "type"],
    "concurrency": 1                            // match OLLAMA_NUM_PARALLEL for ollama
    // "reranker_model": ""                      // optional: e.g. "bge-reranker-v2-m3"
  },

  // ── Security ─────────────────────────────────────────────────────
  "security": {
    // "secret_patterns": [],                    // extra regex patterns to detect secrets
    // "max_file_size_bytes": 1048576,           // skip files larger than this (1 MB)
    // "max_files": 10000                        // max files per project
  },

  // ── Predictive analysis ──────────────────────────────────────────
  "predictive": {
    "enabled": true,
    "weights": {
      "bug":         { "churn": 0.20, "fix_ratio": 0.20, "complexity": 0.20, "coupling": 0.15, "pagerank": 0.10, "authors": 0.15 },
      "tech_debt":   { "complexity": 0.30, "coupling": 0.25, "test_gap": 0.25, "churn": 0.20 },
      "change_risk": { "blast_radius": 0.25, "complexity": 0.20, "churn": 0.20, "test_gap": 0.20, "coupling": 0.15 }
    },
    "cache_ttl_minutes": 60,
    "git_since_days": 180,
    "module_depth": 2
  },

  // ── Intent / domain classification ───────────────────────────────
  "intent": {
    "enabled": false,
    // "domain_hints": {},                       // { "domain_name": ["path/pattern/**"] }
    // "custom_domains": [],                     // [{ "name": "...", "path_patterns": ["..."] }]
    "auto_classify_on_index": true,
    "classify_batch_size": 100
  },

  // ── Runtime tracing (OpenTelemetry) ──────────────────────────────
  "runtime": {
    "enabled": false,
    "otlp": {
      "port": 4318,
      "host": "127.0.0.1",
      "max_body_bytes": 4194304
    },
    "retention": {
      "max_span_age_days": 7,
      "max_aggregate_age_days": 90,
      "prune_interval": 100
    },
    "mapping": {
      "fqn_attributes": ["code.function", "code.namespace", "code.filepath"],
      "route_patterns": ["^(?:GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\\\\s+(.+)$"]
    }
  },

  // ── Cross-repo topology ──────────────────────────────────────────
  "topology": {
    "enabled": true,
    // "repos": [],                              // extra repo paths for subprojects
    "auto_detect": true,
    "auto_discover": true
    // "contract_globs": []                      // globs for API contract files
  },

  // ── Quality gates ────────────────────────────────────────────────
  "quality_gates": {
    "enabled": true,
    "fail_on": "error",                          // "error" | "warning" | "none"
    "rules": {
      // "max_cyclomatic_complexity":       { "threshold": 20, "severity": "error" },
      // "max_coupling_instability":        { "threshold": 0.8, "severity": "warning" },
      // "max_circular_import_chains":      { "threshold": 0, "severity": "error" },
      // "max_dead_exports_percent":        { "threshold": 10, "severity": "warning" },
      // "max_tech_debt_grade":             { "threshold": "C", "severity": "warning" },
      // "max_security_critical_findings":  { "threshold": 0, "severity": "error" },
      // "max_antipattern_count":           { "threshold": 5, "severity": "warning" },
      // "max_code_smell_count":            { "threshold": 10, "severity": "warning" }
    }
  },

  // ── Tool exposure ────────────────────────────────────────────────
  "tools": {
    "preset": "minimal",                         // "minimal" | "standard" | "full" | custom preset name — anything outside the preset is one load_tools call away
    // "include": [],                            // whitelist specific tools
    // "exclude": [],                            // blacklist specific tools
    // "descriptions": {},                       // override tool descriptions
    "description_verbosity": "full",             // "full" | "minimal" | "none"
    "instructions_verbosity": "full",            // "full" | "minimal" | "none"
    "agent_behavior": "off",                     // "strict" | "minimal" | "off" — behavior rules (anti-sycophancy, goal-driven, etc.). Max-tier init sets to "strict".
    "meta_fields": true                          // true | false | ["_hints", "_budget_warning", ...]
  },

  // ── Indexing ignore rules ────────────────────────────────────────
  "ignore": {
    "directories": [],                           // extra directory names to skip
    "patterns": []                               // extra gitignore-style patterns
  },

  // ── Auto-registration (throwaway checkouts) ──────────────────────
  "auto_register": {
    "mode": "always",                            // "always" | "never" | "ask" ("ask" currently behaves as "never"; no interactive prompt exists in the daemon path yet)
    "exclude": []                                // extra globs for throwaway checkouts, e.g. ["~/.gate/worktrees/**", "/tmp/**", "/private/tmp/**", "$TMPDIR/**"]
  },

  // ── Framework-specific ───────────────────────────────────────────
  "frameworks": {
    "laravel": {
      "artisan": { "enabled": true, "timeout": 10000 },
      "graceful_degradation": true
    }
  },

  // ── Logging ───────────────────────────────────────────────────────
  "logging": {
    "file": false,                                 // enable file logging
    "path": "~/.trace/run.log",                    // log file location
    "level": "info",                               // "trace" | "debug" | "info" | "warn" | "error" | "fatal"
    "max_size_mb": 10                              // rotate when log exceeds this size
  },

  // ── File watcher ─────────────────────────────────────────────────
  "watch": {
    "enabled": true,
    "debounceMs": 2000
  },

  // ── Per-project overrides ────────────────────────────────────────
  // Keys are absolute paths; values override any top-level setting for that project.
  // Example:
  // "projects": {
  //   "/path/to/project": {
  //     "ai": { "enabled": true, "concurrency": 4 },
  //     "include": ["src/**/*.ts"],
  //     "exclude": ["dist/**"]
  //   }
  // }
  "projects": {}
}
`;

/** Ensure ~/.trace-mcp/ and ~/.trace-mcp/index/ exist. */
export function ensureGlobalDirs(): void {
  fs.mkdirSync(EPHEMERAL_INDEX_DIR, { recursive: true }); // also creates INDEX_DIR

  // Restrict the data dir + index dir to 0700 so DB sidecars (WAL/SHM that
  // come and go with write activity) are protected by the parent ACL even
  // when their per-file bit is briefly default-umask. No-op on Windows.
  // Inlined (rather than calling shared/db-perms.ts) so this module stays
  // free of cross-module imports — `tests/cli/env-overrides.test.ts` runs
  // global.ts under `node --experimental-strip-types` and can't resolve
  // `.js → .ts` imports of sibling modules.
  if (process.platform !== 'win32') {
    for (const dir of [TRACE_MCP_HOME, INDEX_DIR, EPHEMERAL_INDEX_DIR]) {
      try {
        fs.chmodSync(dir, 0o700);
      } catch {
        /* not ours / not yet present — best-effort */
      }
    }
  }

  // Seed default config on first run so users see all available parameters.
  // 'wx' = O_CREAT|O_EXCL: atomically create-only, no separate existsSync
  // check that could race with another process creating the file first
  // (TRA-256). Same cross-module-import constraint as above rules out the
  // shared src/utils/safe-fs.ts helper here.
  try {
    fs.writeFileSync(GLOBAL_CONFIG_PATH, DEFAULT_CONFIG_JSONC, { flag: 'wx' });
    if (process.platform !== 'win32') {
      try {
        fs.chmodSync(GLOBAL_CONFIG_PATH, 0o600);
      } catch {
        /* best-effort */
      }
    }
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== 'EEXIST') throw err;
  }
}

/** Stable 12-char hex hash of an absolute path. */
export function projectHash(absolutePath: string): string {
  return crypto.createHash('sha256').update(absolutePath).digest('hex').slice(0, 12);
}

/** Sanitized project name from path basename. */
export function projectName(absolutePath: string): string {
  return path.basename(absolutePath).replace(/[^a-zA-Z0-9._-]/g, '_');
}

/** Path to the live session snapshot file (read by PreCompact hook). */
export function getSnapshotPath(projectRoot: string): string {
  const absRoot = path.resolve(projectRoot);
  return path.join(TRACE_MCP_HOME, 'sessions', `${projectHash(absRoot)}-snapshot.json`);
}

/**
 * Compute the default (path-based) global DB path for a project root.
 *
 * This is deliberately NOT identity-aware (see `getProjectRemoteIdentity`
 * below) — it stays a pure function of the absolute path so it keeps
 * producing the exact same value it always has for every project already
 * registered before TRA-38. Identity-based DB *reuse* (recognizing that two
 * different absolute paths are checkouts of the same git remote, e.g. two
 * Multica ephemeral checkouts of the same repo) is resolved one layer up, in
 * `registerProject` (registry.ts): a brand-new root whose remote matches an
 * already-registered different root inherits *that* entry's `dbPath` instead
 * of one computed here. Every read path in this codebase already prefers the
 * registry's stored `dbPath` over recomputing it (see the repeated
 * `resolveDbPath()` helper across `src/cli/*.ts`), so this function only ever
 * actually runs for a root that isn't registered yet — i.e. exactly the
 * "first checkout of this repo, or a non-git / remote-less project" case.
 *
 * TRA-1136: which is also why the ephemeral-subdir choice belongs here and not
 * only in `registerProject`. A one-shot workdir's registry entry is
 * process-local (`_ephemeralEntries`), so every *other* process — and every
 * code path that never registers at all, notably `ProjectManager.addProject`
 * with `persist: false` — fell through to a plain `INDEX_DIR` path and created
 * a DB no sweep could ever reach. That is the 818 MB / 250-file orphan pile;
 * routing on the path shape makes the two agree without a registry lookup.
 */
export function getDbPath(projectRoot: string): string {
  const absRoot = path.resolve(projectRoot);
  const dir = isEphemeralProjectRoot(absRoot) ? EPHEMERAL_INDEX_DIR : INDEX_DIR;
  return path.join(dir, `${projectName(absRoot)}-${projectHash(absRoot)}.db`);
}

/**
 * Resolve the `.git` metadata directory for `root`, following worktree
 * indirection (a `.git` *file* containing `gitdir: <path>`, pointing at
 * `<main-repo>/.git/worktrees/<name>`) the same way `detectGitWorktree` in
 * project-root.ts does. Duplicated here rather than imported — this module
 * must stay free of sibling `.ts` imports (see the comment on
 * `ensureGlobalDirs` above: `tests/cli/env-overrides.test.ts` runs
 * `global.ts` directly under `node --experimental-strip-types`, which can't
 * resolve `.js → .ts` imports of sibling modules).
 */
function resolveGitMetadataDir(root: string): string | null {
  // Trust note (CodeQL js/path-injection): `root` is a project root — CLI
  // argument, cwd, or the `X-Trace-Project` hint on the daemon's loopback
  // `/mcp` endpoint. CodeQL traces that last one and calls it user-provided,
  // which is literally true, but it is the daemon's *own* project-selection
  // input: anyone who can reach the daemon can already ask it to index and
  // serve that whole tree, so reading `<root>/.git/config` grants nothing
  // extra. Reads below never leave `<root>/.git`, and every failure returns
  // null. Whether that loopback endpoint should be authenticated at all is a
  // separate, pre-existing question — see TRA-301.
  const gitEntry = path.join(root, '.git');
  let stat: fs.Stats;
  try {
    stat = fs.statSync(gitEntry); // codeql[js/path-injection]: see trust note above
  } catch {
    return null;
  }
  if (stat.isDirectory()) return gitEntry;
  if (!stat.isFile()) return null;

  let content: string;
  try {
    // codeql[js/file-system-race]: the stat above discriminates dir-vs-file,
    // it is not an existence check — if the entry changes underneath us the
    // read simply throws and we fall back to "not a git repo" (null).
    // codeql[js/path-injection]: see trust note above
    content = fs.readFileSync(gitEntry, 'utf8').trim();
  } catch {
    return null;
  }
  const match = content.match(/^gitdir:\s*(.+)$/);
  if (!match) return null;
  const worktreeAdminDir = path.resolve(root, match[1].trim());
  try {
    // codeql[js/path-injection]: see trust note above
    const raw = fs.readFileSync(path.join(worktreeAdminDir, 'commondir'), 'utf8').trim();
    return path.resolve(worktreeAdminDir, raw);
  } catch {
    // Fallback: admin dir is .git/worktrees/<name>, so ../../ is .git
    return path.resolve(worktreeAdminDir, '../..');
  }
}

/**
 * Read the `origin` remote URL out of a `.git/config` file, falling back to
 * the first `[remote "..."]` section found when there is no `origin`. A
 * small hand-rolled INI reader (not a library, not a `git` subprocess) to
 * keep this module dependency-free and avoid a `git` binary requirement —
 * same rationale as `detectGitWorktree` reading `.git` files directly.
 */
function readGitRemoteUrl(gitDir: string): string | null {
  let configText: string;
  try {
    // codeql[js/path-injection]: `gitDir` comes from resolveGitMetadataDir,
    // which only ever returns a path under the trusted project root — see the
    // trust note there.
    configText = fs.readFileSync(path.join(gitDir, 'config'), 'utf8');
  } catch {
    return null;
  }

  let currentRemote: string | null = null;
  let originUrl: string | null = null;
  let firstUrl: string | null = null;

  for (const line of configText.split(/\r?\n/)) {
    const section = line.match(/^\s*\[remote\s+"([^"]+)"\]\s*$/);
    if (section) {
      currentRemote = section[1];
      continue;
    }
    if (/^\s*\[/.test(line)) {
      currentRemote = null; // left the remote section
      continue;
    }
    if (!currentRemote) continue;
    const urlMatch = line.match(/^\s*url\s*=\s*(.+?)\s*$/);
    if (!urlMatch) continue;
    if (currentRemote === 'origin') originUrl = urlMatch[1];
    if (firstUrl === null) firstUrl = urlMatch[1];
  }

  return originUrl ?? firstUrl;
}

/**
 * Normalize a git remote URL to a canonical `host/org/repo` form so the same
 * repository is recognized regardless of protocol (`https://`, `ssh://`,
 * scp-like `git@host:org/repo`) or a trailing `.git`. Returns null for
 * anything that doesn't look like a `host` + `path` remote (e.g. a local
 * filesystem path used as a remote) — callers fall back to path-based
 * identity in that case, same as a repo with no remote at all.
 */
export function normalizeGitRemote(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  let host: string;
  let rawPath: string;

  if (!trimmed.includes('://')) {
    // scp-like syntax: [user@]host:path
    const scpMatch = trimmed.match(/^(?:[^@/\s]+@)?([^:/\s]+):(.+)$/);
    if (!scpMatch) return null;
    host = scpMatch[1];
    rawPath = scpMatch[2];
  } else {
    try {
      const parsed = new URL(trimmed);
      if (!parsed.hostname) return null;
      host = parsed.hostname;
      rawPath = parsed.pathname;
    } catch {
      return null;
    }
  }

  // Guard against misreading a local path (e.g. `C:\repo`) as an scp-like
  // `host:path` remote — a real git host looks like a DNS name.
  if (!/^[A-Za-z0-9.-]+$/.test(host) || host.length < 2) return null;

  const cleanedPath = rawPath
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .replace(/\.git$/i, '');
  if (!cleanedPath) return null;

  return `${host.toLowerCase()}/${cleanedPath}`;
}

/**
 * Stable identity of the repo at `projectRoot`, derived from its `origin`
 * (or first configured) git remote and normalized so the same repo is
 * recognized across clones/checkouts regardless of which absolute path it
 * lives at (TRA-38) — e.g. Multica's `repo checkout` landing the same repo
 * under a fresh per-run ephemeral directory every time.
 *
 * Returns null for a non-git directory or a git repo with no remote
 * configured; callers fall back to path-based identity in that case, which
 * is exactly today's (pre-TRA-38) behavior — so a project with no
 * resolvable remote is completely unaffected by this function existing.
 */
export function getProjectRemoteIdentity(projectRoot: string): string | null {
  const absRoot = path.resolve(projectRoot);
  const gitDir = resolveGitMetadataDir(absRoot);
  if (!gitDir) return null;
  const url = readGitRemoteUrl(gitDir);
  if (!url) return null;
  return normalizeGitRemote(url);
}

/**
 * Best-effort fingerprint of the commit a checkout currently points at, read
 * straight from the git metadata files (no `git` subprocess, no new imports —
 * see the import-free constraint on this module documented above).
 *
 * Returns `sha:<40-hex>` when HEAD (or the ref it points at) resolves to a
 * commit, `ref:<name>` when HEAD is symbolic but its target ref has no
 * resolvable object (e.g. a fresh clone whose branch ref was never written —
 * two checkouts of the same branch still compare equal), or null when the
 * checkout is not a git work tree / its metadata is unreadable.
 *
 * GH#1371 edge 3 / TRA-1916: `registerProject` (TRA-38) shares one index DB
 * across checkouts with the same git remote, but the `files` table keys rows
 * by repo-relative path — indexing a checkout that sits on a different commit
 * writes branch-divergent files into the canonical checkout's index (and a
 * full-walk reconcile deletes canonical-only rows missing from the clone).
 * Comparing fingerprints before sharing isolates diverged checkouts onto
 * their own DB while same-commit checkouts keep the TRA-38 reuse win.
 */
export function getGitHeadFingerprint(projectRoot: string): string | null {
  const absRoot = path.resolve(projectRoot);
  const dirs = resolveGitHeadDirs(absRoot);
  if (!dirs) return null;
  // Trust note (CodeQL js/path-injection): same project-root input as
  // resolveGitMetadataDir above — reads stay inside the checkout's own git
  // metadata dirs, and every failure returns null.
  let head: string;
  try {
    // codeql[js/path-injection]: see trust note above
    head = fs.readFileSync(path.join(dirs.adminDir, 'HEAD'), 'utf8').trim();
  } catch {
    return null;
  }
  if (!head) return null;
  if (/^[0-9a-fA-F]{40}$/.test(head)) return `sha:${head.toLowerCase()}`;
  const refMatch = head.match(/^ref:\s*(.+)$/);
  if (!refMatch) return `raw:${head}`;
  const refName = refMatch[1].trim();
  const sha = resolveHeadRef(dirs, refName);
  if (sha) return `sha:${sha}`;
  return `ref:${refName}`;
}

/**
 * Locate the per-worktree admin dir (holds this checkout's own HEAD) and the
 * shared common dir (holds branch refs) for `root`. A plain checkout's `.git`
 * directory is both; a linked worktree's `.git` file points at the admin dir
 * and its `commondir` file points back at the shared `.git`. Returns null
 * when `root` is not a git checkout in either layout (bare repos, whose root
 * *is* the git dir, are deliberately unsupported — `getProjectRemoteIdentity`
 * above can't identify them either, so they never reach the sharing path).
 *
 * Mirrors `resolveGitMetadataDir` above but additionally returns the admin
 * dir — HEAD is per-worktree (admin) while branch refs are shared (common),
 * and the fingerprint needs both.
 */
function resolveGitHeadDirs(root: string): { adminDir: string; commonDir: string } | null {
  const gitEntry = path.join(root, '.git');
  let stat: fs.Stats;
  try {
    stat = fs.statSync(gitEntry);
  } catch {
    return null;
  }
  if (stat.isDirectory()) return { adminDir: gitEntry, commonDir: gitEntry };
  if (!stat.isFile()) return null;
  let content: string;
  try {
    content = fs.readFileSync(gitEntry, 'utf8').trim();
  } catch {
    return null;
  }
  const match = content.match(/^gitdir:\s*(.+)$/);
  if (!match) return null;
  const adminDir = path.resolve(root, match[1].trim());
  try {
    const raw = fs.readFileSync(path.join(adminDir, 'commondir'), 'utf8').trim();
    return { adminDir, commonDir: path.resolve(adminDir, raw) };
  } catch {
    return { adminDir, commonDir: path.resolve(adminDir, '../..') };
  }
}

/** Resolve a `refs/...` name to a commit SHA via loose ref files + packed-refs. */
function resolveHeadRef(
  dirs: { adminDir: string; commonDir: string },
  refName: string,
): string | null {
  // A ref name escaping its directory would turn this into an arbitrary file
  // read — resolve relative to the metadata dirs and stay inside them. The
  // content only ever feeds an equality comparison, but containment is free.
  if (!refName || refName.startsWith('/') || refName.split('/').includes('..')) return null;
  for (const dir of [dirs.commonDir, dirs.adminDir]) {
    const refPath = path.resolve(dir, refName);
    if (refPath !== dir && !refPath.startsWith(`${dir}${path.sep}`)) continue;
    try {
      const content = fs.readFileSync(refPath, 'utf8').trim().split(/\s+/)[0] ?? '';
      if (/^[0-9a-fA-F]{40}$/.test(content)) return content.toLowerCase();
    } catch {
      /* not a loose ref here — try packed-refs below */
    }
    try {
      const sha = findRefInPackedRefs(path.join(dir, 'packed-refs'), refName);
      if (sha) return sha;
    } catch {
      /* no packed-refs here */
    }
  }
  return null;
}

/** Scan a packed-refs file for `refName`, skipping comments and peeled lines. */
function findRefInPackedRefs(packedRefsPath: string, refName: string): string | null {
  const text = fs.readFileSync(packedRefsPath, 'utf8');
  for (const line of text.split(/\r?\n/)) {
    if (!line || line.startsWith('#') || line.startsWith('^')) continue;
    const parts = line.split(/\s+/);
    if (parts.length < 2 || parts[1] !== refName) continue;
    if (/^[0-9a-fA-F]{40}$/.test(parts[0])) return parts[0].toLowerCase();
  }
  return null;
}
